import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, LogOut, Bell, FileText, Calendar, DollarSign, Users, Settings, BarChart3, AlertCircle, Store, Menu, X, MessageCircle, Send, Search, Filter, ChevronDown, Landmark, CheckCircle, Truck, LayoutGrid, List } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Notifications from '@/components/Notifications';
import StatusBadge from '@/components/StatusBadge';
import RecentActivity from '@/components/RecentActivity';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  // Restaurar filtros previos desde sessionStorage (al regresar de una factura)
  const STORAGE_KEY = 'dashboard_filters_v1';
  const savedFilters = (() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();

  const [payments, setPayments] = useState([]);
  const [creditsAlert, setCreditsAlert] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(savedFilters.dateFilter || 'today');
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter || 'pendiente');
  const [pendingWhatsApp, setPendingWhatsApp] = useState([]);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  // New state for date range filter
  const [showDateRange, setShowDateRange] = useState(!!(savedFilters.startDate && savedFilters.endDate));
  const [startDate, setStartDate] = useState(savedFilters.startDate || '');
  const [endDate, setEndDate] = useState(savedFilters.endDate || '');
  // Macrobase specific filters
  const [macroProviderSearch, setMacroProviderSearch] = useState(savedFilters.macroProviderSearch || '');
  const [macroStartDate, setMacroStartDate] = useState(savedFilters.macroStartDate || '');
  const [macroEndDate, setMacroEndDate] = useState(savedFilters.macroEndDate || '');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const adminRoles = ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'];

  // Persistir filtros para que al regresar desde una factura se mantengan
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        dateFilter, statusFilter, startDate, endDate,
        macroProviderSearch, macroStartDate, macroEndDate,
      }));
    } catch {
      /* ignore quota errors */
    }
  }, [dateFilter, statusFilter, startDate, endDate, macroProviderSearch, macroStartDate, macroEndDate]);

  useEffect(() => {
    fetchPayments();
    // Load credits alert for admin, gerencia, and encargada_compras
    if (['admin', 'gerencia', 'encargada_compras'].includes(user?.role)) {
      fetchCreditsAlert();
    }
    if (adminRoles.includes(user?.role)) {
      fetchPendingWhatsApp();
    }
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      if (['admin', 'gerencia', 'encargada_compras'].includes(user?.role)) {
        fetchCreditsAlert();
      }
      if (adminRoles.includes(user?.role)) {
        fetchPendingWhatsApp();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [dateFilter, statusFilter, startDate, endDate, macroStartDate, macroEndDate, macroProviderSearch, user]);

  // Al regresar de una factura, hacer scroll y resaltar la tarjeta visitada
  useEffect(() => {
    if (loading || payments.length === 0) return;
    let lastId;
    try { lastId = sessionStorage.getItem('dashboard_last_payment_id'); } catch { return; }
    if (!lastId) return;
    const exists = payments.some(p => p.id === lastId);
    if (!exists) return;
    // Pequeño delay para permitir el render
    const t = setTimeout(() => {
      const el = document.getElementById(`payment-card-${lastId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-emerald-400', 'ring-offset-2');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-emerald-400', 'ring-offset-2');
        }, 2200);
      }
      try { sessionStorage.removeItem('dashboard_last_payment_id'); } catch { /* ignore */ }
    }, 120);
    return () => clearTimeout(t);
  }, [loading, payments]);

  const fetchPayments = async () => {
    try {
      // Special case for pending_macrobase filter
      if (statusFilter === 'pending_macrobase') {
        const params = new URLSearchParams();
        if (macroStartDate) params.append('start_date', macroStartDate);
        if (macroEndDate) params.append('end_date', macroEndDate);
        if (macroProviderSearch.trim()) params.append('provider_name', macroProviderSearch.trim());
        const response = await axios.get(`${API}/payments/pending-macrobase?${params.toString()}`);
        setPayments(response.data);
      } else {
        const params = new URLSearchParams();
        
        // Use date range if both dates are set, otherwise use preset filter
        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        } else if (dateFilter && dateFilter !== 'all') {
          params.append('date_filter', dateFilter);
        }
        
        if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
        
        const response = await axios.get(`${API}/payments?${params.toString()}`);
        setPayments(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeSearch = () => {
    if (startDate && endDate) {
      setDateFilter('custom');
      fetchPayments();
    } else {
      toast.error('Seleccione fecha inicio y fin');
    }
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setDateFilter('today');
    setShowDateRange(false);
  };

  const fetchCreditsAlert = async () => {
    try {
      const response = await axios.get(`${API}/reports/credits-alert`);
      setCreditsAlert(response.data);
    } catch (error) {
      console.error('Error al cargar alertas de crédito');
    }
  };

  const fetchPendingWhatsApp = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp/pending`);
      setPendingWhatsApp(response.data);
    } catch (error) {
      console.error('Error al cargar WhatsApp pendientes');
    }
  };

  const handleRetryWhatsApp = async () => {
    setSendingWhatsApp(true);
    try {
      // Only retry pending messages
      const pendingOnly = pendingWhatsApp.filter(m => m.status === 'pending');
      if (pendingOnly.length === 0) {
        toast.success('No hay mensajes pendientes por enviar');
        setSendingWhatsApp(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/whatsapp/retry-pending`);
      const { sent, failed } = response.data;
      
      if (sent > 0) {
        toast.success(`✅ ${sent} mensaje(s) enviado(s) por WhatsApp`);
      }
      if (failed > 0) {
        toast.error(`❌ ${failed} mensaje(s) no pudieron enviarse (regla 24hrs)`);
      }
      if (sent === 0 && failed === 0) {
        toast.success('No hay mensajes pendientes');
      }
      
      fetchPendingWhatsApp();
    } catch (error) {
      toast.error('Error al reenviar mensajes');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pendingCount = payments.filter(p => p.status === 'pendiente').length;
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  // Toggle Tarjetas / Tabla persistido
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('dashboard_view_mode') || 'cards'; } catch { return 'cards'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard_view_mode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Menu items configuration with categories
  const menuItems = [
    { category: 'Acciones', items: [
      { label: 'Nueva Solicitud', icon: Plus, path: '/payments/new', roles: ['colaborador', 'encargada_compras', 'asistente_admin', 'supervisor'], color: 'bg-emerald-500', iconColor: 'text-white' },
      { label: 'Registrar Deposito', icon: DollarSign, path: '/deposits/register', roles: ['colaborador', 'encargada_compras', 'asistente_admin', 'supervisor', 'admin', 'gerencia'], color: 'bg-teal-500', iconColor: 'text-white' },
    ]},
    { category: 'Reportes', items: [
      { label: 'Reportes', icon: BarChart3, path: '/reports', roles: ['admin', 'gerencia', 'supervisor', 'encargada_compras'], color: 'bg-purple-500', iconColor: 'text-white' },
      { label: 'Validar Banco', icon: Landmark, path: '/bank-validation', roles: ['admin', 'gerencia'], color: 'bg-cyan-500', iconColor: 'text-white' },
      { label: 'Reporte ERP', icon: CheckCircle, path: '/macrobase-report', roles: ['admin', 'gerencia', 'encargada_compras'], color: 'bg-teal-500', iconColor: 'text-white', permission: 'ver_reporte_macrobase' },
      { label: 'Contado Vencidos', icon: AlertCircle, path: '/contado-vencidos', roles: ['admin', 'gerencia', 'encargada_compras'], color: 'bg-red-500', iconColor: 'text-white', permission: 'ver_reporte_contado_vencido' },
      { label: 'Dias de Visita', icon: Truck, path: '/provider-visits', roles: ['admin', 'gerencia', 'encargada_compras'], color: 'bg-orange-500', iconColor: 'text-white', permission: 'ver_reportes' },
      { label: 'Boletas', icon: FileText, path: '/admin/boletas', roles: ['admin', 'gerencia'], color: 'bg-blue-500', iconColor: 'text-white' },
    ]},
    { category: 'Administracion', items: [
      { label: 'Proveedores', icon: FileText, path: '/admin/providers', roles: ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'], color: 'bg-orange-500', iconColor: 'text-white' },
      { label: 'Tiendas', icon: Store, path: '/admin/stores', roles: ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'], color: 'bg-pink-500', iconColor: 'text-white' },
      { label: 'Bancos', icon: Store, path: '/admin/bancos', roles: ['admin', 'gerencia'], color: 'bg-indigo-500', iconColor: 'text-white' },
    ]},
    { category: 'Sistema', items: [
      { label: 'Usuarios', icon: Users, path: '/admin/users', roles: ['admin'], color: 'bg-slate-600', iconColor: 'text-white' },
      { label: 'WhatsApp', icon: MessageCircle, path: '/admin/whatsapp', roles: ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'], color: 'bg-green-500', iconColor: 'text-white', badge: pendingWhatsApp.length },
      { label: 'Configuracion', icon: Settings, path: '/admin/settings', roles: ['admin'], color: 'bg-gray-500', iconColor: 'text-white' },
    ]},
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950" data-testid="dashboard-page">
      {/* Page header simple - el header global vive en AppShell */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-slate-100" data-testid="dashboard-title">Inicio</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Hola, {user?.name} {user?.role === 'admin' && '(Administrador)'}</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Credits Alert for Admin, Gerencia, and Encargada Compras */}
        {['admin', 'gerencia', 'encargada_compras'].includes(user?.role) && creditsAlert.length > 0 && (
          <Card className="p-4 mb-6 bg-orange-50 dark:bg-amber-950/40 border-orange-200 dark:border-amber-900/60">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-orange-600 dark:text-amber-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 dark:text-amber-200 mb-2">
                  ⚠️ Alertas de Crédito - Facturas Próximas a Vencer
                </h3>
                <div className="space-y-2">
                  {creditsAlert.slice(0, 5).map(credit => (
                    <div 
                      key={credit.id} 
                      className={`text-sm cursor-pointer hover:bg-orange-100 dark:hover:bg-amber-900/40 p-2 rounded transition-colors ${
                        credit.days_remaining <= 0 ? 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 font-bold' : 
                        credit.days_remaining <= 3 ? 'bg-orange-100 dark:bg-amber-900/30 text-orange-800 dark:text-amber-300' : 'text-orange-800 dark:text-amber-200'
                      }`}
                      onClick={() => navigate(`/payments/${credit.id}`)}
                    >
                      <span className="font-medium">{credit.proveedor_nombre || credit.proveedor}</span> - 
                      Q{credit.monto_depositar.toFixed(2)} - 
                      {credit.days_remaining <= 0 ? (
                        <span className="text-red-600 dark:text-red-400 font-bold">⚠️ VENCIDO hace {Math.abs(credit.days_remaining)} día(s)</span>
                      ) : (
                        <span>Vence en {credit.days_remaining} día{credit.days_remaining !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                  ))}
                  {creditsAlert.length > 5 && (
                    <Button variant="link" className="text-orange-700 dark:text-amber-300 p-0 h-auto" onClick={() => navigate('/reports')}>
                      Ver {creditsAlert.length - 5} más...
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
          <Card className="p-6 border-slate-200/70 dark:border-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-br from-white to-blue-50/50 dark:from-slate-900 dark:to-blue-950/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">Total de Pagos</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="total-payments">{payments.length}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-200/70 dark:from-blue-950/60 dark:to-blue-900/40 rounded-2xl p-4 shadow-inner">
                <FileText className="h-8 w-8 text-[#2c57c5] dark:text-blue-300" />
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200/70 dark:border-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-br from-white to-amber-50/40 dark:from-slate-900 dark:to-amber-950/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">Pagos Pendientes</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="pending-payments">{pendingCount}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-orange-200/70 dark:from-amber-950/60 dark:to-amber-900/40 rounded-2xl p-4 shadow-inner">
                <Calendar className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200/70 dark:border-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 bg-gradient-to-br from-white to-blue-50/40 dark:from-slate-900 dark:to-blue-950/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">Monto Total</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100" data-testid="total-amount">
                  Q{payments.reduce((sum, p) => sum + p.monto_depositar, 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-300/70 dark:from-blue-950/60 dark:to-blue-800/40 rounded-2xl p-4 shadow-inner">
                <DollarSign className="h-8 w-8 text-[#1e3a8a] dark:text-blue-300" />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Main filters row */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={dateFilter} onValueChange={(val) => { setDateFilter(val); if (val !== 'custom') { setStartDate(''); setEndDate(''); setShowDateRange(false); } }}>
                <SelectTrigger className="w-[180px]" data-testid="date-filter">
                  <SelectValue placeholder="Filtrar por fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="yesterday">Ayer</SelectItem>
                  <SelectItem value="week">Última semana</SelectItem>
                  <SelectItem value="month">Último mes</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                  {startDate && endDate && <SelectItem value="custom">📅 Rango personalizado</SelectItem>}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="pagada">Pagada</SelectItem>
                  {/* Mostrar filtro Macrobase basado en permisos del usuario */}
                  {(user?.permissions || []).includes('ver_filtro_macrobase') && (
                    <SelectItem value="pending_macrobase">Sin ingresar a ERP</SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDateRange(!showDateRange)}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Search className="mr-2 h-4 w-4" />
                Buscar por fechas
                <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${showDateRange ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {['colaborador', 'encargada_compras', 'asistente_admin', 'supervisor', 'admin', 'gerencia'].includes(user?.role) && (
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button
                  onClick={() => navigate('/deposits/register')}
                  variant="outline"
                  className="border-[#2c57c5] text-[#2c57c5] hover:bg-blue-50 hover:border-[#1e3a8a] w-full sm:w-auto shadow-sm"
                  data-testid="register-deposit-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Registrar Depósito</span>
                  <span className="sm:hidden">Depósito</span>
                </Button>
                {['colaborador', 'encargada_compras', 'asistente_admin', 'supervisor'].includes(user?.role) && (
                  <Button
                    onClick={() => navigate('/payments/new')}
                    className="bg-gradient-to-r from-[#1e3a8a] to-[#2c57c5] hover:from-[#1e3a8a] hover:to-[#1e40af] text-white w-full sm:w-auto shadow-md"
                    data-testid="new-payment-button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Nuevo Pago</span>
                    <span className="sm:hidden">Pago</span>
                  </Button>
                )}
              </div>
            )}
          </div>
          
          {/* Date range picker (collapsible) */}
          {showDateRange && (
            <Card className="p-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/60">
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="startDate" className="text-sm font-medium text-slate-700 dark:text-slate-300">Fecha inicio</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="endDate" className="text-sm font-medium text-slate-700 dark:text-slate-300">Fecha fin</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDateRangeSearch} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Search className="mr-2 h-4 w-4" />
                    Buscar
                  </Button>
                  <Button variant="outline" onClick={clearDateRange}>
                    Limpiar
                  </Button>
                </div>
              </div>
              {startDate && endDate && (
                <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  📅 Mostrando pagos del {format(new Date(startDate + 'T12:00:00'), 'dd MMM yyyy', { locale: es })} al {format(new Date(endDate + 'T12:00:00'), 'dd MMM yyyy', { locale: es })}
                </p>
              )}
            </Card>
          )}

          {/* Macrobase Filter Panel */}
          {statusFilter === 'pending_macrobase' && (
            <Card className="p-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/60" data-testid="macrobase-filter-panel">
              <h4 className="font-semibold text-amber-900 dark:text-amber-200 mb-3 flex items-center">
                <Filter className="mr-2 h-4 w-4" />
                Filtros ERP Pendiente
              </h4>
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Proveedor</Label>
                  <Input
                    placeholder="Buscar proveedor..."
                    value={macroProviderSearch}
                    onChange={(e) => setMacroProviderSearch(e.target.value)}
                    className="mt-1"
                    data-testid="macro-provider-search"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Desde</Label>
                  <Input
                    type="date"
                    value={macroStartDate}
                    onChange={(e) => setMacroStartDate(e.target.value)}
                    className="mt-1"
                    data-testid="macro-start-date"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Hasta</Label>
                  <Input
                    type="date"
                    value={macroEndDate}
                    onChange={(e) => setMacroEndDate(e.target.value)}
                    className="mt-1"
                    data-testid="macro-end-date"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => { setMacroProviderSearch(''); setMacroStartDate(''); setMacroEndDate(''); }}
                  className="border-amber-500 text-amber-700 dark:text-amber-300"
                  data-testid="macro-clear-filters"
                >
                  Limpiar
                </Button>
              </div>
              {/* Macrobase Summary */}
              {payments.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-amber-200 dark:border-amber-900/60">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Facturas Pendientes</p>
                    <p className="text-lg font-bold text-amber-700 dark:text-amber-300" data-testid="macro-total-count">{payments.length}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-amber-200 dark:border-amber-900/60">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Monto Total Pendiente</p>
                    <p className="text-lg font-bold text-amber-700 dark:text-amber-300" data-testid="macro-total-amount">Q{payments.reduce((s, p) => s + (p.monto_depositar || 0), 0).toLocaleString('es-GT', {minimumFractionDigits: 2})}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-amber-200 dark:border-amber-900/60">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Proveedores</p>
                    <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{new Set(payments.map(p => p.proveedor_nombre || p.proveedor)).size}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-amber-200 dark:border-amber-900/60">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Credito Pendiente</p>
                    <p className="text-lg font-bold text-red-600 dark:text-red-400">Q{payments.filter(p => p.tipo_pago === 'credito').reduce((s, p) => s + (p.monto_depositar || 0), 0).toLocaleString('es-GT', {minimumFractionDigits: 2})}</p>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Payments List */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {['admin', 'gerencia', 'encargada_compras', 'asistente_admin'].includes(user?.role) 
                  ? 'Todos los Pagos' 
                  : user?.role === 'supervisor' 
                    ? 'Pagos de Mi Equipo' 
                    : 'Mis Pagos'
                }
              </h2>
              {/* Toggle Tarjetas / Tabla */}
              <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" role="tablist" data-testid="view-mode-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'cards' ? 'bg-blue-50 dark:bg-blue-950/40 text-[#2c57c5] dark:text-blue-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  data-testid="view-mode-cards"
                  aria-pressed={viewMode === 'cards'}
                >
                  <LayoutGrid className="h-3.5 w-3.5" /> Tarjetas
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-slate-200 dark:border-slate-800 transition-colors ${viewMode === 'table' ? 'bg-blue-50 dark:bg-blue-950/40 text-[#2c57c5] dark:text-blue-300' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  data-testid="view-mode-table"
                  aria-pressed={viewMode === 'table'}
                >
                  <List className="h-3.5 w-3.5" /> Tabla
                </button>
              </div>
            </div>

            {loading ? (
              <Card className="p-8 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <p className="text-slate-600 dark:text-slate-400">Cargando pagos...</p>
              </Card>
            ) : payments.length === 0 ? (
              <Card className="p-8 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="no-payments-message">
                <FileText className="h-12 w-12 text-slate-400 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">No hay pagos para mostrar</p>
                {['colaborador', 'encargada_compras', 'asistente_admin'].includes(user?.role) && (
                  <Button
                    onClick={() => navigate('/payments/new')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    Crear tu primer pago
                  </Button>
                )}
              </Card>
            ) : viewMode === 'table' ? (
              <Card className="overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="payments-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Proveedor</th>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Tienda</th>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Estado</th>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Tipo</th>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Banco</th>
                        <th className="text-right font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Monto</th>
                        <th className="text-left font-semibold px-4 py-3 uppercase text-[10px] tracking-wider">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {payments.map((payment) => (
                        <tr
                          key={payment.id}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                          onClick={() => {
                            try { sessionStorage.setItem('dashboard_last_payment_id', payment.id); } catch { /* ignore */ }
                            navigate(`/payments/${payment.id}`);
                          }}
                          data-testid={`payment-row-${payment.id}`}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900 dark:text-slate-100">{payment.proveedor_nombre || payment.proveedor}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-500">{payment.nit_proveedor}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{payment.tienda_nombre || payment.centro_costo || '—'}</td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              status={payment.is_overdue && payment.status === 'pendiente' ? 'vencido' : payment.status}
                              size="sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={`text-[10px] rounded-full ${payment.tipo_pago === 'credito' ? 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60' : 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60'}`}>
                              {payment.tipo_pago === 'credito' ? 'Crédito' : 'Contado'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{payment.banco}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100 tabular-nums">Q{payment.monto_depositar.toLocaleString('es-GT', {minimumFractionDigits: 2})}</td>
                          <td className="px-4 py-3 text-slate-500 dark:text-slate-500 whitespace-nowrap">{format(new Date(payment.created_at), 'dd MMM yy', { locale: es })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <div className="grid gap-3">
                {payments.map((payment) => {
                  const statusColor = payment.status === 'pagada' || payment.status === 'aprobada' 
                    ? 'border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20'
                    : payment.status === 'rechazada' 
                      ? 'border-l-red-500 bg-red-50/30 dark:bg-red-950/20'
                      : 'border-l-amber-400 bg-white dark:bg-slate-900';
                  
                  return (
                  <Card
                    key={payment.id}
                    id={`payment-card-${payment.id}`}
                    className={`border-l-4 border-slate-200 dark:border-slate-800 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ${statusColor}`}
                    onClick={() => {
                      try { sessionStorage.setItem('dashboard_last_payment_id', payment.id); } catch { /* ignore */ }
                      navigate(`/payments/${payment.id}`);
                    }}
                    data-testid={`payment-card-${payment.id}`}
                  >
                    <div className="p-4">
                      {/* Top row: Provider + Amount */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">{payment.proveedor_nombre || payment.proveedor}</h3>
                          <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">{payment.tienda_nombre || payment.centro_costo}</p>
                        </div>
                        <div className="text-right ml-4 shrink-0">
                          <p className="text-lg font-bold text-slate-900 dark:text-slate-100">Q{payment.monto_depositar.toLocaleString('es-GT', {minimumFractionDigits: 2})}</p>
                        </div>
                      </div>
                      
                      {/* Badges row */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <StatusBadge
                          status={payment.is_overdue && payment.status === 'pendiente' ? 'vencido' : payment.status}
                          size="sm"
                        />
                        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full ${payment.tipo_pago === 'credito' ? 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60' : 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60'}`}>
                          {payment.tipo_pago === 'credito' ? 'Credito' : 'Contado'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] px-2 py-0.5 rounded-full border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400">
                          {payment.forma_pago === 'efectivo' ? 'Efectivo' : payment.forma_pago === 'cheque' ? 'Cheque' : 'Transferencia'}
                        </Badge>
                        {payment.is_overdue && payment.status === 'pendiente' && payment.days_overdue > 0 && (
                          <span className="text-[10px] font-semibold text-red-700 dark:text-red-400">
                            · {payment.days_overdue}d vencido
                          </span>
                        )}
                        {['pagada', 'aprobada'].includes(payment.status) && !payment.ingresada_macrobase && (
                          <Badge variant="outline" className="text-[10px] px-2 py-0.5 rounded-full border-yellow-300 text-yellow-700 bg-yellow-50 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900/60">
                            Pend. ERP
                          </Badge>
                        )}
                      </div>
                      
                      {/* Bottom row: details */}
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 dark:text-slate-500">
                        <span>Banco: {payment.banco}</span>
                        <span>NIT: {payment.nit_proveedor}</span>
                        <span>{format(new Date(payment.created_at), 'dd MMM yyyy', { locale: es })}</span>
                        {payment.fecha_pago_programada && (
                          <span className={payment.is_overdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                            Prog: {format(new Date(payment.fecha_pago_programada), 'dd MMM yyyy', { locale: es })}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right column: Recent activity */}
          <aside className="hidden xl:block">
            <div className="sticky top-20">
              <RecentActivity limit={8} />
            </div>
          </aside>
        </div>

        {/* Actividad reciente para móvil/tablet (debajo de la lista) */}
        <div className="xl:hidden mt-6">
          <RecentActivity limit={6} compact />
        </div>
      </main>
    </div>
  );
}
